import React, { useState } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { parseThreadInventory, isThreadInStock } from '../utils/parseThreadInventory';

const PageContainer = styled.div`
  padding: 40px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  margin-top: 80px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h1`
  color: #62E07D;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  color: white;
  line-height: 1.8;
`;

const Button = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 16px 32px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  transition: background-color 0.3s ease;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:hover {
    background-color: #50B366;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
  margin: 24px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #62E07D, #50B366);
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-weight: 600;
  font-size: 12px;
`;

const StatusMessage = styled.div`
  color: ${props => props.error ? '#FF6B6B' : props.success ? '#62E07D' : 'white'};
  padding: 16px;
  background: ${props => props.error ? 'rgba(255, 107, 107, 0.1)' : props.success ? 'rgba(98, 224, 125, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  margin-top: 16px;
  border: 1px solid ${props => props.error ? '#FF6B6B' : props.success ? '#62E07D' : 'rgba(255, 255, 255, 0.2)'};
`;

function UpdateThreadInventory() {
  const [updating, setUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleUpdate = async () => {
    setUpdating(true);
    setError('');
    setSuccess(false);
    setProgress(0);

    try {
      // Get inventory data
      const inventory = parseThreadInventory();
      
      setStatus('Loading existing threads from database...');

      // Fetch all existing threads
      const threadsRef = collection(db, 'threads');
      const snapshot = await getDocs(threadsRef);
      
      if (snapshot.empty) {
        setError('No threads found in database. Please run Import first.');
        setUpdating(false);
        return;
      }

      const threads = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setStatus(`Found ${threads.length} threads. Updating inventory data...`);

      // Update threads in batches
      const batchSize = 50;
      let updated = 0;
      let inStockCount = 0;
      let matched = 0;
      let notMatched = 0;

      for (let i = 0; i < threads.length; i += batchSize) {
        const batch = threads.slice(i, i + batchSize);
        
        // Update each thread in the batch
        await Promise.all(
          batch.map(async (thread) => {
            const quantity = inventory[thread.threadNumber];
            
            if (quantity !== undefined) {
              matched++;
              const inStock = isThreadInStock(quantity);
              if (inStock) inStockCount++;

              const threadRef = doc(db, 'threads', thread.id);
              await updateDoc(threadRef, {
                inStock: inStock,
                quantity: quantity > 0 ? quantity : null,
                updatedAt: new Date()
              });
            } else {
              notMatched++;
              // Thread not in inventory - ensure it's marked as out of stock
              const threadRef = doc(db, 'threads', thread.id);
              await updateDoc(threadRef, {
                inStock: false,
                quantity: null,
                updatedAt: new Date()
              });
            }
          })
        );

        updated += batch.length;
        const progressPercent = Math.round((updated / threads.length) * 100);
        setProgress(progressPercent);
        setStatus(
          `Updated ${updated} of ${threads.length} threads...\n` +
          `Matched: ${matched} | Not in inventory: ${notMatched} | In Stock: ${inStockCount}`
        );
      }

      setSuccess(true);
      setStatus(
        `✅ Successfully updated ${updated} threads!\n\n` +
        `• ${inStockCount} threads marked as IN STOCK\n` +
        `• ${matched} threads matched with inventory\n` +
        `• ${notMatched} threads not in inventory (marked as out of stock)`
      );
      setProgress(100);
    } catch (err) {
      console.error('Update error:', err);
      setError(`Error during update: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <PageContainer>
      <Title>Update Thread Inventory</Title>
      
      <InfoBox>
        <div style={{ marginBottom: '16px', fontSize: '14px' }}>
          <strong>Update existing threads with your current inventory data</strong>
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
          • This will UPDATE your existing threads (no duplicates created)<br />
          • Threads with 2+ cones will be marked as "in stock"<br />
          • Quantities will be set from your inventory spreadsheet<br />
          • Threads not in your inventory will be marked as "out of stock"<br />
          • Safe to run multiple times - it only updates, doesn't create new threads
        </div>
      </InfoBox>

      <Button onClick={handleUpdate} disabled={updating || success}>
        {updating ? 'Updating...' : success ? 'Update Complete!' : 'Update Inventory Data'}
      </Button>

      {(updating || progress > 0) && (
        <ProgressBar>
          <ProgressFill progress={progress}>
            {progress}%
          </ProgressFill>
        </ProgressBar>
      )}

      {status && (
        <StatusMessage success={success} error={!!error}>
          {status.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </StatusMessage>
      )}

      {error && (
        <StatusMessage error>
          {error}
        </StatusMessage>
      )}

      {success && (
        <InfoBox style={{ marginTop: '24px', borderColor: '#62E07D' }}>
          <div style={{ fontSize: '14px' }}>
            <strong>Next Steps:</strong><br />
            1. Go to Thread Manager to view your updated threads<br />
            2. Use the "In Stock" filter to see your available threads<br />
            3. Update storage locations as needed<br />
            4. Use the Matcher to find closest thread colors!
          </div>
        </InfoBox>
      )}
    </PageContainer>
  );
}

export default UpdateThreadInventory;



